import { ViewController } from '@estee/elc-service';
import { action, observable, computed } from 'mobx';
import { Config } from '~domain/entities/Config';
import { StoresMap } from '~domain/entities/StoresMap';
import { UserLocationRepository, INavigatorPosition } from '~repositories/UserLocationRepository';
import { IStoresMapConfig, IAddressSearch } from '~interfaces/IStoresInterfaces';
import { diContainer } from '~setup/diContainer';
import serviceNames from '~setup/StoresService';
import { IGeoLocation } from '~domain/entities/UserLocation';
import {
    IAddressSelectedStrategy,
    IMapPanPosition,
    BuyOnlinePickupInStoreAddressSelectedStrategy
} from '../../strategies/StoresLandingAddressSelectedStrategies';
import { UrlFormatter } from '@estee/elc-formatters';
import { PickUpInStoreViewController } from '../PickUpInStoreViewController';
import { BopisStoresFiltersViewController } from './BopisStoresFiltersViewController';
import { StoreDeliveryOptions } from '~constants/constants';
import { events, emit } from '@estee/elc-service-bus';

export class BopisFindStoreModalViewController extends ViewController {
    @observable private storesMap: StoresMap;
    @observable public configService: Config;
    @observable public userLocationRepository: UserLocationRepository;
    private bopisStoresFiltersViewController: BopisStoresFiltersViewController;
    private pickUpInStoreViewController: PickUpInStoreViewController;
    private fetchDataStrategy: IAddressSelectedStrategy;
    private urlFormatter: UrlFormatter;

    constructor(
        storesMap: StoresMap,
        fetchDataStrategy: IAddressSelectedStrategy = new BuyOnlinePickupInStoreAddressSelectedStrategy()
    ) {
        super();
        this.storesMap = storesMap;
        this.configService = diContainer.get(serviceNames.config);
        this.userLocationRepository = diContainer.get(serviceNames.userLocationRepository);
        this.fetchDataStrategy = fetchDataStrategy;
        this.urlFormatter = diContainer.get(serviceNames.urlFormatter);

        this.bopisStoresFiltersViewController = diContainer.get(
            serviceNames.bopisStoresFiltersViewController
        );
        this.pickUpInStoreViewController = diContainer.get(
            serviceNames.pickUpInStoreViewController
        );
    }

    @action
    public loaded = async (mapConfig: IStoresMapConfig) => {
        try {
            await this.userLocationRepository.getUserLocation();
            const { geolocation: userLocation } = this.userLocationRepository.userLocation;
            const { mapBounds, mapCenter, mapZoom } = mapConfig;
            const { storesDefaultSearchLocation } = this.configService.config;

            if (userLocation) {
                await this.updateMapLocation(userLocation);
            } else if (storesDefaultSearchLocation) {
                await this.updateMapLocation(storesDefaultSearchLocation);
            } else {
                mapBounds && this.storesMap.convertBounds(mapBounds);
                mapCenter && this.storesMap.updateMapCenter(mapCenter);
                mapZoom && this.storesMap.updateMapZoom(mapZoom);
            }
        } finally {
            this.isLoading = false;
        }
    };

    public onAddressSelected = async (searchAddress: IAddressSearch) => {
        await this.pickUpInStoreViewController.onAddressSelected(searchAddress);
        this.bopisStoresFiltersViewController.updateUserZipCode();
    };

    public onMapPan = async (center: IMapPanPosition) => {
        await this.fetchDataStrategy.onMapPan(center);
    };

    public onLocationReset = async (position: INavigatorPosition) => {
        this.pickUpInStoreViewController.onLocationReset(position);
    };

    @action
    public setSelectedStore = async (storeId: string) => {
        this.pickUpInStoreViewController.setSelectedStore(storeId);
    };

    @action
    public storeHoursToggled = async (isExpanded: boolean, name: string) => {
        emit(events.STORE_OPENING_HOURS_TOGGLED, {
            eventAction: isExpanded
                ? StoreDeliveryOptions.STORE_OPENING_HOURS_EXPAND
                : StoreDeliveryOptions.STORE_OPENING_HOURS_COLLAPSE,
            eventLabel: name
        });
    };

    @computed
    private get breadcrumbsUrls() {
        return {
            homeUrl: this.urlFormatter.langPrefixUrl('/')
        };
    }

    @computed
    get data() {
        return {
            config: this.configService.config,
            breadcrumbsUrls: this.breadcrumbsUrls
        };
    }

    private async updateMapLocation(geolocation: IGeoLocation) {
        this.storesMap.updateMapCenter({
            lat: String(geolocation.lat),
            lng: String(geolocation.lng)
        });

        return this.fetchDataStrategy.onUpdateMapLocation(geolocation);
    }
}
